// Data-size unit handling for balances coming off the API.
//
// The server reports the two halves of a data balance in *different* units:
// `balance.size` carries the package's display unit (Asset.sizeDisplayUnit, e.g. GB), while
// `balance.availableBalance` is re-derived from raw bytes on every read and flips to MB as soon
// as less than 1 GB is left (ActivatedItemMapper.setAvailableBalance on the server). Anything
// comparing the two has to normalise first, or a 5 GB package with 800 MB left reads as
// "800 of 5" - i.e. as good as full, with the low-data warning never firing.
//
// Multipliers are binary to mirror the server's ByteCalculator, which is 1024-based.

interface Size {
    sizeValue: number;
    sizeUnit: string;
}

const UNIT_MULTIPLIERS: Record<string, number> = {
    B: 1,
    KB: 1024,
    MB: 1024 ** 2,
    GB: 1024 ** 3,
    TB: 1024 ** 4
};

// A balance denominated in something other than bytes - a credits wallet uses the purchase
// currency as its "unit" - has no byte equivalent, so callers fall back to the raw values.
export function isDataUnit(unit: string | undefined | null): boolean {
    return !!unit && unit.toUpperCase() in UNIT_MULTIPLIERS;
}

export function toBytes(size: Size | undefined | null): number | null {
    if (!size || !isDataUnit(size.sizeUnit)) return null;
    return size.sizeValue * UNIT_MULTIPLIERS[size.sizeUnit.toUpperCase()];
}

export function convertSize(value: number, from: string, to: string): number | null {
    if (!isDataUnit(from) || !isDataUnit(to)) return null;
    if (from.toUpperCase() === to.toUpperCase()) return value;
    return (value * UNIT_MULTIPLIERS[from.toUpperCase()]) / UNIT_MULTIPLIERS[to.toUpperCase()];
}
